import type { WeightPointView } from '@steado/admin-contract/user-detail';

interface WeightChartProps {
  readonly points: readonly WeightPointView[];
}

const WIDTH = 640;
const HEIGHT = 140;
const PADDING = 8;

/**
 * The weigh-in trend, drawn as inline SVG.
 *
 * The design spec's stack table names Recharts for charts, and the screens that
 * need real chart types (AI spend, token histograms) will use it. This is a
 * thirty-point line over one series: a charting library would add a dependency
 * an order of magnitude larger than the component it replaced, and one that
 * needs a measured container to render at all — which is exactly the thing that
 * does not work in jsdom. Recharts arrives with the first screen that earns it.
 *
 * The series is a `role="img"` with a summarising label plus a visually hidden
 * list of the readings, so the trend is available to a screen reader instead of
 * being a decorative path.
 */
export function WeightChart({ points }: WeightChartProps) {
  if (points.length === 0) {
    return <p className="text-[13px] text-muted-foreground">No weigh-ins in this window.</p>;
  }

  const weights = points.map((point) => point.weightKg);
  const min = Math.min(...weights);
  const max = Math.max(...weights);
  // A flat series would divide by zero; give it a band so the line sits centred.
  const span = max - min === 0 ? 1 : max - min;

  const first = points[0];
  const last = points[points.length - 1];
  const change = last === undefined || first === undefined ? 0 : last.weightKg - first.weightKg;

  const coordinates = points.map((point, index) => {
    const x =
      points.length === 1 ? WIDTH / 2 : PADDING + (index / (points.length - 1)) * (WIDTH - PADDING * 2);
    const y = HEIGHT - PADDING - ((point.weightKg - min) / span) * (HEIGHT - PADDING * 2);
    return { x, y, point };
  });

  const label = `Weight trend: ${String(points.length)} readings, ${String(min)} to ${String(max)} kilograms, ${
    change === 0
      ? 'no net change'
      : `${change > 0 ? 'up' : 'down'} ${String(Math.abs(round1(change)))} kilograms`
  }.`;

  return (
    <figure className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between">
        <span className="font-mono text-[12.5px] text-foreground">{String(last?.weightKg ?? min)} kg</span>
        <span
          className={`font-mono text-[12px] ${change > 0 ? 'text-warn-fg' : change < 0 ? 'text-ok-fg' : 'text-muted-foreground'}`}
        >
          {change === 0 ? 'no change' : `${change > 0 ? '+' : ''}${String(round1(change))} kg`}
        </span>
      </div>

      <svg
        role="img"
        aria-label={label}
        viewBox={`0 0 ${String(WIDTH)} ${String(HEIGHT)}`}
        preserveAspectRatio="none"
        className="h-[140px] w-full overflow-visible"
      >
        <polyline
          points={coordinates.map(({ x, y }) => `${String(x)},${String(y)}`).join(' ')}
          fill="none"
          stroke="var(--color-primary)"
          strokeWidth={1.5}
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
        {coordinates.map(({ x, y, point }) => (
          <circle key={point.localDate} cx={x} cy={y} r={2} fill="var(--color-primary)" />
        ))}
      </svg>

      <figcaption className="flex items-baseline justify-between font-mono text-[11px] text-muted-foreground">
        <span>{first?.localDate}</span>
        <span>{last?.localDate}</span>
      </figcaption>

      <ul className="sr-only">
        {points.map((point) => (
          <li key={point.localDate}>{`${point.localDate}: ${String(point.weightKg)} kg`}</li>
        ))}
      </ul>
    </figure>
  );
}

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}
